let brandLogoMapCache: Record<string, string> | null = null;

const loadBrandLogoMap = async (): Promise<Record<string, string>> => {
  if (brandLogoMapCache) {
    return brandLogoMapCache;
  }

  try {
    const response = await fetch('/brand.json');
    brandLogoMapCache = await response.json();
  } catch (error) {
    console.error('Error loading brand logos:', error);
    brandLogoMapCache = {};
  }

  return brandLogoMapCache!;
};

export const preloadBrandLogoCSV = async (): Promise<void> => {
  await loadBrandLogoMap();
};

// brand.json references many files that don't exist in public/logos (it was
// generated against a larger source), so every candidate is verified before
// use. Cached per path; the GET also warms the HTTP cache for the <img> tag.
const pathExistsCache = new Map<string, boolean>();

const imageExists = async (path: string): Promise<boolean> => {
  const cached = pathExistsCache.get(path);
  if (cached !== undefined) return cached;
  let ok = false;
  try {
    const res = await fetch(path);
    // Dev server answers missing files with index.html (200), so check the type.
    const type = res.headers.get('content-type') || '';
    ok = res.ok && type.startsWith('image');
  } catch {
    ok = false;
  }
  pathExistsCache.set(path, ok);
  return ok;
};

export const getLogoPath = async (brandName: string): Promise<string> => {
  const logoMap = await loadBrandLogoMap();

  const normalizedBrandName = brandName.trim().toUpperCase().replace(/[''´`'"]/g, '').replace(/\s+/g, ' ');

  let filename = logoMap[normalizedBrandName];

  if (!filename) {
    for (const key of Object.keys(logoMap)) {
      const normalizedKey = key.replace(/[''´`'"]/g, '').replace(/\s+/g, ' ');
      if (normalizedKey === normalizedBrandName) {
        filename = logoMap[key];
        break;
      }
    }
  }

  // Candidates: the mapped file first, then the logo repo's own naming
  // convention (UPPER_SNAKE.<ext>) derived from the brand name.
  const candidates: string[] = [];
  if (filename) candidates.push(`/logos/${filename}`);
  const derived = normalizedBrandName.replace(/ /g, '_');
  for (const ext of ['png', 'jpg', 'jpeg', 'svg', 'webp']) {
    candidates.push(`/logos/${derived}.${ext}`);
  }

  for (const candidate of Array.from(new Set(candidates))) {
    if (await imageExists(candidate)) return candidate;
  }

  const initial = brandName.charAt(0).toUpperCase();
  const svg = `<svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"><rect width="40" height="40" fill="#f97316"/><text x="20" y="22" font-size="20" font-weight="600" fill="white" text-anchor="middle" dominant-baseline="middle" font-family="Arial, sans-serif">${initial}</text></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};
